import { useEffect, useState } from 'react';

interface PageLoaderProps {
  onLoadComplete?: () => void;
}

export default function PageLoader({ onLoadComplete }: PageLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        const increment = Math.random() * 15 + 5;
        return Math.min(prev + increment, 100);
      });
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (progress >= 100) {
      setTimeout(() => {
        setIsComplete(true);
        onLoadComplete?.();
      }, 300);
      
      setTimeout(() => {
        setIsHidden(true);
      }, 800);
    }
  }, [progress, onLoadComplete]);

  if (isHidden) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-[#0A0A0F] flex flex-col items-center justify-center transition-all duration-500 ${
        isComplete ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Logo Animation */}
      <div className="relative mb-8">
        <div className={`w-24 h-24 flex items-center justify-center transition-transform duration-500 ${isComplete ? 'scale-110' : 'scale-100'}`}>
          <img 
            src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/d509b5c7881d9da6657335b51d03b561.png"
            alt="RMind Logo"
            className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(232,74,31,0.6)]"
          />
        </div>
        
        {/* Rotating Ring */}
        <div className="absolute inset-[-12px] border-2 border-[#E84A1F]/30 rounded-3xl animate-[spin_3s_linear_infinite]">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-[#E84A1F] rounded-full"></div>
        </div>
        
        {/* Pulse Effect */}
        <div className="absolute inset-0 bg-[#E84A1F]/10 rounded-2xl animate-ping"></div>
      </div>

      {/* Progress Bar */}
      <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-[#E84A1F] to-[#F06030] rounded-full transition-all duration-300 relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1s_infinite]"></div>
        </div>
      </div>

      {/* Loading Text */}
      <div className="flex items-center gap-2">
        <span className="text-white/60 text-sm tracking-widest">LOADING</span>
        <span className="text-[#E84A1F] font-bold">{Math.round(progress)}%</span>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-[#E84A1F]/10 rounded-full animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-[#F06030]/10 rounded-full animate-pulse delay-300"></div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
